import React from "react";
import {
    AppBar, Toolbar, Typography, Box, Chip, Tooltip,
    IconButton, Menu, Divider, Avatar,
} from "@mui/material";
import WalletBalanceChip from "../billing/WalletBalanceChip";
import { useBilling } from "../../context/BillingContext";
import { useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import {
    Email as EmailIcon,
    WhatsApp as WhatsAppIcon,
    Business as BusinessIcon,
    CalendarToday as CalendarIcon,
    ContactSupport as SupportIcon,
    KeyboardArrowDown,
    CheckCircleOutline,
    WarningAmberOutlined,
    ErrorOutline,
} from "@mui/icons-material";

// ─── Page title map ───────────────────────────────────────────────────────────
const PAGE_TITLES = {
    "/":                                    "Dashboard",
    "/dashboard":                           "Dashboard",
    "/upload":                              "Upload & Share",
    "/send-whatsapp":                       "Send WhatsApp",
    "/profile":                             "Profile",
    "/credits-info":                        "Credits Information",
    "/billing":                             "Billing",
    "/billing/ledger":                      "Billing ledger",
    "/billing/topups":                      "Billing top-ups",
    "/template-type1":                      "Watermark Templates",
    "/template-type2":                      "Without Watermark Templates",
    "/personal-whatsapp":                   "Personal WhatsApp",
    "/message-queue":                       "Message Queue",
    "/group-add":                           "Manage Groups",
    "/email-queue":                         "Email Queue",
    "/email-schedule":                      "Bulk Email",
    "/email-analytics":                     "Email Analytics",
    "/whatsapp-business":                   "WhatsApp Business",
    "/whatsapp-business/accounts":          "Business Accounts",
    "/whatsapp-business/phone-numbers":     "Phone Numbers",
    "/whatsapp-business/templates":         "Message Templates",
    "/whatsapp-business/send-message":      "Send Messages",
    "/whatsapp-business/campaign":          "Campaign",
    "/whatsapp-business/message-speed":     "Message Speed",
    "/whatsapp-business/groups":            "Manage Groups",
    "/whatsapp-business/media":             "Media Management",
    "/whatsapp-business/profile":           "Business Profile",
    "/whatsapp-business/limits":            "Messaging Limits",
    "/whatsapp-business/settings":          "Whatsapp Business Settings",
    "/whatsapp-business/bot-training":      "AI Agent",
    "/whatsapp-business/templates/create":  "Create Template",
    "/whatsapp-business/templates/edit":    "Edit Template",
    "/crm/dashboard":                       "CRM Dashboard",
    "/crm/analytics":                       "CRM Analytics",
    "/crm/pipeline":                        "Deals",
    "/crm/stages":                          "Manage stages",
    "/crm/tags":                            "Create Tags",
    "/crm/contacts":                        "CRM Contacts",
    "/crm/contacts/":                       "Contact",
    "/crm/tasks":                           "Tasks",
    "/crm/products-services":               "Products / Services",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (d) => {
    if (!d) return "—";
    try {
        return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch { return "—"; }
};

const daysLeft = (d) => {
    if (!d) return null;
    try {
        const diff = Math.ceil((new Date(d) - new Date()) / 86400000);
        return diff < 0 ? 0 : diff;
    } catch { return null; }
};

const statusOf = (days) => {
    if (days === null) return null;
    if (days <= 0)  return "expired";
    if (days < 7)   return "critical";
    if (days < 30)  return "warning";
    return "ok";
};

const STATUS_META = {
    ok:       { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", Icon: CheckCircleOutline,    label: "Active"        },
    warning:  { color: "#d97706", bg: "#fffbeb", border: "#fde68a", Icon: WarningAmberOutlined,  label: "Expiring soon" },
    critical: { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", Icon: ErrorOutline,          label: "Urgent"        },
    expired:  { color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb", Icon: ErrorOutline,          label: "Expired"       },
};

const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : name.slice(0, 2).toUpperCase();
};

// ─── Component ────────────────────────────────────────────────────────────────
const Header = () => {
    const location = useLocation();
    const { isCreditBased } = useBilling();
    const [anchorEl, setAnchorEl] = React.useState(null);

    const logo     = Cookies.get("logo")?.trim();
    const userName = Cookies.get("userName") || Cookies.get("name") || "User";
    const bese     = Cookies.get("bese");
    const pwse     = Cookies.get("pwse");
    const bwse     = Cookies.get("bwse");

    const besDays = daysLeft(bese);
    const pwsDays = daysLeft(pwse);
    const bwsDays = daysLeft(bwse);

    const pageTitle = PAGE_TITLES[location.pathname]
        ?? Object.entries(PAGE_TITLES)
            .filter(([k]) => k !== "/" && location.pathname.startsWith(k))
            .sort((a, b) => b[0].length - a[0].length)[0]?.[1]
        ?? "";

    const subscriptions = [
        { key: "email", label: "Email",    icon: <EmailIcon sx={{ fontSize: 13 }} />,    days: besDays, date: bese },
        { key: "wa",    label: "WhatsApp", icon: <WhatsAppIcon sx={{ fontSize: 13 }} />, days: pwsDays, date: pwse },
        { key: "bwa",   label: "Biz WA",   icon: <BusinessIcon sx={{ fontSize: 13 }} />, days: bwsDays, date: bwse },
    ].filter((s) => s.date);

    const urgentCount = subscriptions.filter((s) => {
        const st = statusOf(s.days);
        return st === "critical" || st === "expired";
    }).length;

    return (
        <AppBar
            position="static"
            elevation={0}
            sx={{
                backgroundColor: "#ffffff",
                borderBottom: "1px solid #e5e7eb",
                flexShrink: 0,
            }}
        >
            <Toolbar
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: { xs: 2, sm: 3 },
                    minHeight: "64px !important",
                    height: "64px",
                    justifyContent: "flex-end",
                }}
            >
                {/* Left — page title (commented out, shown in sidebar instead) */}
                {/* <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: "#111827", letterSpacing: "-0.01em", fontSize: "0.95rem" }}
                >
                    {pageTitle}
                </Typography> */}

                {/* Right */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {isCreditBased && <WalletBalanceChip />}

                    {/* Subscription chips stay on fixed plans */}
                    {!isCreditBased && subscriptions.length > 0 && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            {subscriptions.map(({ key, label, icon, days }) => {
                                const st   = statusOf(days);
                                const meta = STATUS_META[st];
                                return (
                                    <Tooltip
                                        key={key}
                                        title={
                                            <Box sx={{ p: 0.5 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
                                                    {label} Subscription
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)", display: "block" }}>
                                                    {days > 0 ? `${days} days remaining` : "Expired"}
                                                </Typography>
                                            </Box>
                                        }
                                        arrow
                                    >
                                        <Chip
                                            icon={icon}
                                            label={days !== null ? (days > 0 ? `${days}d` : "Exp") : "—"}
                                            size="small"
                                            sx={{
                                                height: 24,
                                                fontSize: "0.7rem",
                                                fontWeight: 700,
                                                backgroundColor: meta.bg,
                                                color: meta.color,
                                                border: `1px solid ${meta.border}`,
                                                "& .MuiChip-icon": { color: meta.color, ml: "6px" },
                                                "& .MuiChip-label": { px: "6px" },
                                            }}
                                        />
                                    </Tooltip>
                                );
                            })}

                            {/* Details toggle */}
                            <Tooltip title="Subscription details">
                                <IconButton
                                    size="small"
                                    onClick={(e) => setAnchorEl(e.currentTarget)}
                                    sx={{
                                        width: 28, height: 28,
                                        backgroundColor: urgentCount > 0 ? "#fef2f2" : "#f3f4f6",
                                        border: `1px solid ${urgentCount > 0 ? "#fecaca" : "#e5e7eb"}`,
                                        borderRadius: 1.5,
                                        color: urgentCount > 0 ? "#dc2626" : "#6b7280",
                                        "&:hover": { backgroundColor: urgentCount > 0 ? "#fee2e2" : "#e5e7eb" },
                                    }}
                                >
                                    <KeyboardArrowDown sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}

                    {/* Company logo */}
                    {logo && (
                        <Box
                            component="img"
                            src={logo}
                            alt="Company Logo"
                            loading="lazy"
                            sx={{
                                height: 34,
                                borderRadius: 1,
                                border: "1px solid #e5e7eb",
                                p: "3px",
                                objectFit: "contain",
                            }}
                        />
                    )}

                    {/* User avatar — commented out */}
                    {/* <Avatar
                        sx={{
                            width: 32, height: 32,
                            fontSize: "0.72rem", fontWeight: 700,
                            backgroundColor: "#1a2333", color: "#fff",
                            cursor: "default",
                        }}
                    >
                        {getInitials(userName)}
                    </Avatar> */}
                </Box>
            </Toolbar>

            {/* ── Subscription details dropdown ────────────────────────────── */}
            <Menu
                anchorEl={anchorEl}
                open={!isCreditBased && Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                PaperProps={{
                    elevation: 4,
                    sx: { width: 320, borderRadius: 2.5, mt: 0.75, overflow: "hidden" },
                }}
            >
                {/* Header row */}
                <Box sx={{ px: 2.5, py: 1.75, backgroundColor: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#111827" }}>
                        Subscription Status
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {subscriptions.length} active plan{subscriptions.length !== 1 ? "s" : ""}
                    </Typography>
                </Box>

                {/* Per-plan rows */}
                {subscriptions.map(({ key, label, icon, days, date }, i) => {
                    const st         = statusOf(days);
                    const meta       = STATUS_META[st];
                    const StatusIcon = meta.Icon;
                    return (
                        <React.Fragment key={key}>
                            {i > 0 && <Divider />}
                            <Box sx={{ px: 2.5, py: 1.75, display: "flex", alignItems: "center", gap: 1.5 }}>
                                {/* Icon tile */}
                                <Box
                                    sx={{
                                        width: 36, height: 36, borderRadius: 2, flexShrink: 0,
                                        backgroundColor: meta.bg, border: `1px solid ${meta.border}`,
                                        display: "grid", placeItems: "center", color: meta.color,
                                    }}
                                >
                                    {icon}
                                </Box>

                                {/* Name + date */}
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#111827" }}>
                                        {label}
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.2 }}>
                                        <CalendarIcon sx={{ fontSize: 11, color: "text.disabled" }} />
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDate(date)}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Status badge */}
                                <Box sx={{ textAlign: "right" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, justifyContent: "flex-end" }}>
                                        <StatusIcon sx={{ fontSize: 13, color: meta.color }} />
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: meta.color }}>
                                            {days > 0 ? `${days}d` : "Expired"}
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem" }}>
                                        {meta.label}
                                    </Typography>
                                </Box>
                            </Box>
                        </React.Fragment>
                    );
                })}

                {/* Footer */}
                <Box
                    sx={{
                        px: 2.5, py: 1.5,
                        backgroundColor: "#f9fafb",
                        borderTop: "1px solid #e5e7eb",
                        display: "flex", alignItems: "center", gap: 1,
                    }}
                >
                    <SupportIcon sx={{ fontSize: 15, color: "text.disabled" }} />
                    <Typography variant="caption" color="text.secondary">
                        Contact support to renew or extend your plan
                    </Typography>
                </Box>
            </Menu>
        </AppBar>
    );
};

export default Header;
